import { Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// Radix Select forbids an empty string item value, so we use a sentinel for the
// "All" option and translate it back to '' (the hook's "no filter" value).
const ALL_VALUE = '__all__'

/**
 * A single compact control row for an employee table: a search input, one
 * filter `Select` per filter definition (each defaulting to "All"), a "Clear"
 * button shown only while search or filters are active, and an `N of M` count
 * shown while rows are filtered. Sorting is reset from the sortable header's
 * three-state cycle. Styling mirrors the existing toolbars (`h-7`, `text-xs`,
 * shadcn/ui components) so it blends into each table's toolbar.
 *
 * @param {Object} props
 * @param {string} props.search - Current search query.
 * @param {(value: string) => void} props.onSearchChange - Search input handler.
 * @param {Array<{key: string, label: string, options?: string[]}>} props.filters
 *   Filter definitions. Each `options` list is rendered as select items; when a
 *   definition omits `options`, that filter's select is skipped.
 * @param {Record<string, string>} props.activeFilters - Map of filter key -> active value ('' = All).
 * @param {(key: string, value: string) => void} props.onFilterChange - Filter change handler.
 * @param {() => void} props.onClear - Clears search + filters.
 * @param {boolean} props.isActive - Whether search or any filter is active.
 * @param {number} props.activeCount - Number of rows in the current view.
 * @param {number} props.totalCount - Total number of source rows.
 * @param {string} [props.searchPlaceholder] - Placeholder for the search input.
 * @param {React.ReactNode} [props.actionAfterFilters] - Optional action rendered immediately after filter selects.
 * @param {string} [props.className] - Extra classes for the row container.
 */
export default function EmployeeTableControls({
  search,
  onSearchChange,
  filters = [],
  activeFilters = {},
  onFilterChange,
  onClear,
  isActive,
  activeCount,
  totalCount,
  searchPlaceholder = 'Search by ID or name…',
  actionAfterFilters,
  className,
}) {
  const rowClassName = ['flex flex-wrap items-center gap-2', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rowClassName}>
      <div className="relative w-full sm:w-56">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="text"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Search employees"
          className="h-7 pl-7"
        />
      </div>

      {filters.map((filter) => {
        const options = filter.options ?? []
        if (options.length === 0) return null
        const value = activeFilters[filter.key] || ALL_VALUE

        return (
          <Select
            key={filter.key}
            value={value}
            onValueChange={(next) =>
              onFilterChange(filter.key, next === ALL_VALUE ? '' : next)
            }
          >
            <SelectTrigger
              size="sm"
              className="h-7 text-xs"
              aria-label={`Filter by ${filter.label}`}
            >
              <SelectValue placeholder={`All ${filter.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{`All ${filter.label}`}</SelectItem>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      })}

      {actionAfterFilters}

      {isActive && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7"
          onClick={onClear}
        >
          Clear
        </Button>
      )}

      {isActive && (
        <span className="text-xs text-muted-foreground">
          {activeCount} of {totalCount}
        </span>
      )}
    </div>
  )
}
